import React, { useState } from 'react'
import { Box, Typography, Button, styled, Chip, CircularProgress } from '@mui/material'
import { Check, Upgrade, Receipt, ManageAccounts } from '@mui/icons-material'
import { useLocalStorage } from 'usehooks-ts'
import PricingModal from '../../PricingModal'
import { useSubscription } from '@/hooks/common/useSubscription'
import { CognitoUser } from '@/types'

// ==================== STYLED COMPONENTS ====================
const SectionHeader = styled(Box)({
  marginBottom: '32px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
})

const SectionDescription = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  lineHeight: 1.6,
})

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '16px',
})

const CardHeader = styled(Box)({
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
})

const CardContent = styled(Box)({
  padding: '24px',
})

const PlanCard = styled(Box)<{ active?: boolean }>(({ active }) => ({
  padding: '24px',
  borderRadius: '12px',
  border: active ? '2px solid #3b82f6' : '2px solid #e5e7eb',
  backgroundColor: active ? '#eff6ff' : '#ffffff',
  position: 'relative',
}))

const FeatureList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '20px',
})

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

// ==================== COMPONENT ====================
const BillingSection: React.FC = () => {
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  const [ewUser] = useLocalStorage<CognitoUser | null>('ewUser', null)
  const { subscription, loading, tier, isPro, openPortal, portalLoading } = useSubscription(ewUser?.userID)

  const planLabel     = tier.charAt(0).toUpperCase() + tier.slice(1)
  const renewalDate   = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null
  const statusLabel   = subscription?.cancelAtPeriodEnd ? 'Cancels at period end' : subscription?.status ?? 'Active'
  const statusActive  = subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  return (
    <>
      <SectionHeader>
        <SectionTitle>Billing</SectionTitle>
        <SectionDescription>Manage your subscription and billing information</SectionDescription>
      </SectionHeader>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Current Plan
            </Typography>
            <Chip
              label={statusActive ? 'Active' : statusLabel}
              size='small'
              sx={{
                backgroundColor: statusActive ? '#d1fae5' : '#fef3c7',
                color: statusActive ? '#065f46' : '#92400e',
                fontWeight: 600,
                fontSize: '11px',
              }}
            />
          </Box>
        </CardHeader>
        <CardContent>
          <PlanCard active>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  {planLabel}
                </Typography>
                {renewalDate && (
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>
                    {subscription?.cancelAtPeriodEnd ? `Ends ${renewalDate}` : `Renews ${renewalDate}`}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isPro && (
                  <Button
                    variant='outlined'
                    startIcon={portalLoading ? <CircularProgress size={14} /> : <ManageAccounts />}
                    onClick={openPortal}
                    disabled={portalLoading}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:hover': { borderColor: '#9ca3af' },
                    }}
                  >
                    Manage Billing
                  </Button>
                )}
                {!isPro && (
                  <Button
                    variant='contained'
                    startIcon={<Upgrade />}
                    onClick={() => setPricingModalOpen(true)}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: '#3b82f6',
                      '&:hover': { backgroundColor: '#2563eb' },
                    }}
                  >
                    Upgrade to Pro
                  </Button>
                )}
              </Box>
            </Box>

            {tier === 'free' && (
              <FeatureList>
                {['3 patterns per month', 'Standard exports', 'Community support'].map((feature) => (
                  <FeatureItem key={feature}>
                    <Check sx={{ fontSize: 18, color: '#10b981' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#4b5563' }}>
                      {feature}
                    </Typography>
                  </FeatureItem>
                ))}
              </FeatureList>
            )}

            {isPro && (
              <FeatureList>
                {['Unlimited patterns', 'All premium materials & accents', 'High-res exports', 'No watermarks'].map((feature) => (
                  <FeatureItem key={feature}>
                    <Check sx={{ fontSize: 18, color: '#10b981' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#4b5563' }}>
                      {feature}
                    </Typography>
                  </FeatureItem>
                ))}
              </FeatureList>
            )}
          </PlanCard>
        </CardContent>
      </Card>

      {/* Billing History — managed via Stripe portal for paid users */}
      {!isPro && (
        <Card>
          <CardHeader>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Billing History
            </Typography>
          </CardHeader>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Receipt sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6b7280', mb: 1 }}>
                No billing history yet
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#9ca3af' }}>
                Your invoices will appear here once you upgrade
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <PricingModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        userId={ewUser?.userID}
      />
    </>
  )
}

export default BillingSection
